import i18n from '@/i18n';
import { nowMs, toDate, toEpochMs } from './clock';

/**
 * Date, duration, and countdown formatting.
 *
 * Everything here takes an explicit `at` instant (defaulting to the console `Clock`) so a
 * component's rendered output is a pure function of its props in tests. Nothing in this module
 * reads the wall clock itself — `./clock` is the only file allowed to.
 */

const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const TIME_ONLY = new Intl.DateTimeFormat('en-GB', {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

const DATE_ONLY = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
});

/** {@link DATE_ONLY}, pinned to Bangkok — see {@link formatCalendarDay} for why it must be. */
const BANGKOK_DATE_ONLY = new Intl.DateTimeFormat('en-GB', {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  timeZone: 'Asia/Bangkok',
});

const DATE_TIME_COMPACT = new Intl.DateTimeFormat('en-GB', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/** `23 Jul 2026, 14:05` — the default absolute timestamp rendering. */
export function formatDateTime(iso: string): string {
  return DATE_TIME.format(toDate(iso));
}

/** `14:05:03` — used inside timelines where the date is already established by a heading. */
export function formatTime(iso: string): string {
  return TIME_ONLY.format(toDate(iso));
}

/** `23 Jul 2026`. */
export function formatDate(iso: string): string {
  return DATE_ONLY.format(toDate(iso));
}

/**
 * A `YYYY-MM-DD` calendar day, as `23 Jul 2026` — for days the *server* has already decided.
 *
 * Every other formatter here renders an instant in the viewer's own zone, which is right for an
 * instant: two operators watching the same incident from different places should each see their
 * own clock. A calendar day is the opposite kind of value. The clearing daily breakdown's day is
 * cut at midnight **Asia/Bangkok** server-side (see `ObligationDayTotal`), and re-reading it in
 * the browser's zone would shift half the world's operators onto the day before — a table whose
 * rows sum to a position dated differently from the position.
 *
 * So the zone is pinned rather than defaulted: the string is anchored at UTC noon-adjacent
 * midnight and printed back in Bangkok, which lands on the named day from any browser.
 */
export function formatCalendarDay(day: string): string {
  return BANGKOK_DATE_ONLY.format(toDate(`${day}T00:00:00Z`));
}

/**
 * `5 Aug, 08:00` — an absolute instant compact enough to appear twice inside one sentence.
 *
 * The year is dropped on purpose: this exists for the dashboard's custom KPI range, which is
 * capped at 30 days, so both ends are always within a month or so of today and the year would be
 * the same noise on both sides of the dash. Like every formatter here it renders in the browser's
 * local zone, which is the zone the operator typed the range in.
 */
export function formatDateTimeCompact(iso: string): string {
  return DATE_TIME_COMPACT.format(toDate(iso));
}

/**
 * A compact duration: `4d 6h`, `2h 14m`, `9m 31s`, `47s`.
 *
 * Always two units at most — an SLA countdown that reads `1d 4h 22m 08s` is harder to scan at a
 * glance than one that reads `1d 4h`, and precision below the leading unit is noise.
 */
export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(total / 86_400);
  const h = Math.floor((total % 86_400) / 3_600);
  const m = Math.floor((total % 3_600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
  return `${s}s`;
}

/**
 * A `mm:ss` clock face for short deadlines (reservation TTL, evidence deadline).
 *
 * Rolls over to `h:mm:ss` past an hour so the reservation panel and the 24-hour review SLA can
 * share one component without either looking wrong.
 */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3_600);
  const m = Math.floor((total % 3_600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** A screen-reader-friendly expansion of {@link formatCountdown}. */
export function describeDuration(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3_600);
  const m = Math.floor((total % 3_600) / 60);
  const s = total % 60;
  const parts: string[] = [];
  if (h > 0) parts.push(i18n.t('common.hours', { count: h }));
  if (m > 0) parts.push(i18n.t('common.minutes', { count: m }));
  if (s > 0 || parts.length === 0) parts.push(i18n.t('common.seconds', { count: s }));
  return parts.join(' ');
}

/**
 * `just now`, `6m ago`, `3h ago`, `2d ago`, or `in 4m` for future instants.
 *
 * @param iso - the instant to describe.
 * @param at - the reference instant in epoch milliseconds. Defaults to the console clock.
 */
export function formatRelative(iso: string, at: number = nowMs()): string {
  const delta = at - toEpochMs(iso);
  const abs = Math.abs(delta);
  if (abs < 45_000) return i18n.t('common.justNow');
  const label = formatDuration(abs);
  return delta >= 0
    ? i18n.t('common.ago', { value: label })
    : i18n.t('common.inFuture', { value: label });
}

/** Milliseconds from `at` until `iso`. Negative once the deadline has passed. */
export function msUntil(iso: string, at: number = nowMs()): number {
  return toEpochMs(iso) - at;
}

/** Milliseconds elapsed since `iso`, measured from `at`. */
export function msSince(iso: string, at: number = nowMs()): number {
  return at - toEpochMs(iso);
}

/** Age of a case or transaction as a compact duration, e.g. `4h 12m`. */
export function formatAge(iso: string, at: number = nowMs()): string {
  return formatDuration(Math.max(0, msSince(iso, at)));
}
